import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { buildArticleJsonLd } from '@/lib/seo';
import { kaspreadsheetUrl } from '@/mocks/categories';

export default function BaseTaoCoupon() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <JsonLd data={buildArticleJsonLd({ headline: 'BaseTao Coupon Codes & Discount Guide 2026: How to Save on Every Purchase', description: 'Find, apply, and maximize BaseTao coupon codes and discounts through seasonal promotions and bulk order incentives.', path: '/basetao-coupon' })} />
      <Navbar />
      <main className="pt-20">
        <article>
          <header className="bg-background-100 py-14 md:py-20">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
                <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
                <span>/</span>
                <span className="text-foreground-700">Coupon</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                BaseTao Coupon Codes & Discount Guide 2026: How to Save on Every Purchase
              </h1>
              <p className="text-base md:text-lg text-foreground-600 max-w-3xl leading-relaxed mb-6">
                Discover how to find, apply, and maximize BaseTao coupon codes and discounts. Learn about seasonal promotions, bulk order incentives, and proven strategies to reduce your total sourcing costs through smart coupon usage.
              </p>
              <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
                <span>Updated: August 2026</span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span>10 min read</span>
              </div>
            </div>
          </header>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="space-y-10 text-foreground-700">
              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">How BaseTao Coupons and Discounts Work</h2>
                <p className="text-sm md:text-base leading-relaxed mb-3">
                  BaseTao Spreadsheet includes a built-in coupon and discount tracking system that automatically scans available promotions across its partner network and applies eligible discounts to your spreadsheet totals. Unlike manually searching for coupon codes — a time-consuming process with uncertain results — the platform&apos;s automated system continuously monitors promotional databases and instantly flags savings opportunities the moment they become available for your tracked products.
                </p>
                <p className="text-sm md:text-base leading-relaxed mb-3">
                  The coupon system supports multiple discount types including percentage-off codes, fixed-amount discounts, free shipping promotions, and bulk order incentives. When multiple coupons are available for a single product, the system automatically applies the combination that yields the highest total savings. It also alerts you when limited-time codes are about to expire, ensuring you never miss a savings window.
                </p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">Types of Discounts Available</h2>
                <div className="space-y-4">
                  {[
                    { title: 'Seasonal Promotional Events', desc: 'Major shopping events throughout the year typically feature the deepest discounts. BaseTao Spreadsheet tracks these events across all partner platforms and surfaces the best available prices for your tracked products during promotional periods.' },
                    { title: 'Volume-Based Discounts', desc: 'When you order larger quantities of the same product, many suppliers offer tiered pricing that reduces the per-unit cost. The platform automatically calculates whether increasing your order quantity would unlock a lower price tier, helping you optimize for total cost rather than just the displayed unit price.' },
                    { title: 'New User Welcome Offers', desc: 'First-time users of integrated partner platforms often qualify for welcome discounts. BaseTao Spreadsheet identifies these opportunities and notifies you when products in your tracking list are eligible for new-user promotional pricing.' },
                    { title: 'Loyalty and Repeat Purchase Incentives', desc: 'Regular buyers through partner platforms may qualify for loyalty discounts, cashback rewards, or exclusive promotional access. The platform tracks your purchasing patterns and flags when you become eligible for loyalty-based savings programs.' },
                  ].map((item) => (
                    <div key={item.title} className="bg-background-100 rounded-lg p-5 border border-background-200/60">
                      <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="bg-accent-50 rounded-lg p-6 md:p-8 border border-accent-100">
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-3">Start Saving Today</h2>
                <p className="text-sm text-foreground-700 leading-relaxed mb-5">
                  Browse our <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">homepage</Link> for additional savings strategies and the latest BaseTao Spreadsheet resources.
                </p>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <a href={kaspreadsheetUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer">
                    Explore Discounts
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-external-link-line text-sm"></i></div>
                  </a>
                  <Link to="/" className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer">
                    Back to Homepage
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}